<script lang="ts" setup>
  import { useBreakpoints } from '@storefront/storefront';
  import { StoreLayout, AccountLayout } from '@storefront/components';

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  interface JournalEntry {
    remainingPoints: number,
    createdAt: string,
    expiredAt: string
  }

  interface Props {
    journal: JournalEntry[];
    totalPoints: number;
    totalExpired: number;
    ratio: string;
    value: string;
  }

  defineProps<Props>();

  const breakpoints = useBreakpoints();
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.LOYALTY_PROGRAM') }}
  </AccountMobileNavigation>
  <SfTile class="account-loyalty" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account-heading">
        {{ $t('SHOP.ACCOUNT.HEADER.LOYALTY_PROGRAM') }}
      </h1>
    </template>
    <template #default>
      <section v-if="totalPoints">
        <h2 class="account-loyalty__total">
          {{ $t('SHOP.ACCOUNT.LABEL.LOYALTY_POINTS') }} {{ totalPoints }} {{ $t('SHOP.UNIT.POINTS_SHORT') }}
        </h2>
        <p class="account-loyalty__convert">
          {{ $t('SHOP.ACCOUNT.LABEL.LOYALTY_POINTS_EXCHANGED') }}
          <b class="account-loyalty__money">
            <SfMoney :value="value" />
          </b>
        </p>
        <p class="account-loyalty__expired">
          {{ $t('SHOP.ACCOUNT.LABEL.LOYALTY_POINTS_INACTIVE') }} <b>{{ totalExpired }} {{ $t('SHOP.UNIT.POINTS_SHORT') }}</b>
        </p>
      </section>

      <section v-else class="account-loyalty__lack-section">
        <h2 class="account-loyalty__lack">
          {{ $t('SHOP.ACCOUNT.LABEL.LOYALTY_POINTS_NONE') }}
        </h2>
      </section>

      <section v-if="journal.length > 0" class="account-loyalty__table-wrapper">
        <h2 id="account-loyalty__table-heading" class="account-loyalty__table-heading">
          {{ $t('SHOP.ACCOUNT.HEADER.LOYALTY_POINTS_HISTORY') }}
        </h2>
        <div aria-labelledby="account-loyalty__table-heading" class="account-loyalty__table" role="table">
          <div role="rowgroup">
            <div class="account-loyalty__table-head account-loyalty__row account-loyalty__row--head" role="row">
              <div class="account-loyalty__cell account-loyalty__cell-count account-loyalty__cell--head" role="columnheader">
                {{ $t('SHOP.ACCOUNT.LABEL.LOYALTY_POINTS_QUANTITY') }}
              </div>
              <div class="account-loyalty__cell account-loyalty__cell-created account-loyalty__cell--head" role="columnheader">
                {{ $t('SHOP.ACCOUNT.LABEL.LOYALTY_POINTS_ACQUIRED_AT') }}
              </div>
              <div class="account-loyalty__cell account-loyalty__cell-expired account-loyalty__cell--head" role="columnheader">
                {{ $t('SHOP.ACCOUNT.LABEL.LOYALTY_POINTS_EXPIRES_AT') }}
              </div>
            </div>
          </div>
          <div class="account-loyalty__body" role="rowgroup">
            <div v-for="item in journal" class="account-loyalty__row account-loyalty__row--body" role="row">
              <div class="account-loyalty__cell account-loyalty__cell-count account-loyalty__cell--body" role="cell">
                {{ item.remainingPoints }}
              </div>
              <div class="account-loyalty__cell account-loyalty__cell-created account-loyalty__cell--body" role="cell">
                <SfDateTime :value="item.createdAt" />
              </div>
              <div class="account-loyalty__cell account-loyalty__cell-expired account-loyalty__cell--body" role="cell">
                <SfDateTime :value="item.expiredAt" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <SfDataPlaceholder v-else>
        {{ $t('SHOP.ACCOUNT.MESSAGE.LOYALTY_POINTS_HISTORY_PLACEHOLDER') }}
      </SfDataPlaceholder>
    </template>
  </SfTile>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-loyalty {
    &__total,
    &__table-heading {
      font-size: 1.25rem;
    }

    &__convert {
      margin-top: 1.25rem;
    }

    &__expired {
      margin-top: 0.5rem;
    }

    &__table-head {
      background-color: var(--sf-account-color-light-background);
      font-weight: var(--font-weight-semi);
      border-top: 1px solid var(--sf-account-loyalty-table-border-color);
    }

    &__body {
      font-size: 0.875rem;
    }

    &__row--body:nth-child(2n) {
      background-color: var(--sf-account-color-light-background);
    }

    &__row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      border-bottom: 1px solid var(--sf-account-loyalty-table-border-color);
    }

    &__cell {
      padding: 0.5rem 0.625rem;
    }

    &__table-heading {
      margin-bottom: 1.25rem;
    }
  }

  @include media.query(lg) {

    .account-loyalty {
      --sf-tile-large-padding: 2rem;

      &__lack {
        text-align: center;
      }

      &__total, &__table-heading {
        font-size: 1.25rem;
      }

      &__convert, &__expired {
        font-size: 0.875rem;
      }

      &__table-wrapper {
        margin-top: 1rem;
      }

      &__convert {
        margin-top: 1rem;
      }

      &__expired {
        margin-top: 0.25rem;
      }

      &__cell--head {
        font-size: 0.875rem;
      }

      &__cell {
        padding: 0.5rem 0.375rem;
      }
    }
  }
</style>
